import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { ApolloError, useMutation } from "@apollo/client";
import { sendCode, sendCodeVariables } from "@layout/modals/graphql/__generated__/sendCode";
import { SEND_CODE } from "@layout/modals/graphql/SEND_CODE";
import { FormTypeEnum, LoginAcceptForm, LoginTypeEnum } from "@layout/modals/types";
import { getErrorByCode, setAuthParamToURL } from "@layout/modals/utils";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";

import DigitCodeInput from "@/components/common/Inputs/digitCodeInput";
import {
  getInitiateCodeState,
  isCodeLengthValid,
} from "@/components/common/Inputs/digitCodeInput/utils";
import {
  getReferralKeyFromCookies,
  removeReferralKeyFromCookies,
} from "@/components/layout/Template/utils";
import AuthorizationFormContext from "@/contexts/AuthorizationForm/AuthorizationFormContext";
import GlobalContext from "@/contexts/Global/GlobalContext";
import { GlobalContextType } from "@/contexts/Global/types";
import { useSmartCaptcha } from "@/contexts/SmartCaptcha/useSmartCaptcha";
import UserContext from "@/contexts/User/UserContext";
import useAuthorize from "@/hooks/useAuthorize";
import { authorizeVariables } from "@/hooks/useAuthorize/graphql/__generated__/authorize";
import { useManageUtmData } from "@/hooks/useManageUtmData";
import { getErrorCode } from "@/utils/apolloUtils";
import { Button, ButtonColorEnum, ButtonSizeEnum } from "@components/common/button";
import Timer from "@components/common/Timer";
import { SEND_OTP_CODE_RETRY_DELAY, UrlParamKeyEnum } from "@components/constants";

import "./styles.scss";

const FormWithCodeModal = ({ loginType, isFirstLogin }: LoginAcceptForm) => {
  const location = useLocation();
  const { isExpert } = useContext<GlobalContextType>(GlobalContext);
  const firstCodeInputRef = useRef<HTMLInputElement>(null);
  const { executeCaptcha, prepareCaptcha } = useSmartCaptcha();

  // Капча монтируется только когда нужна (SEO #12): подгружаем её при открытии формы,
  // чтобы к отправке внешний скрипт уже был готов и пользователь не ждал.
  useEffect(() => {
    prepareCaptcha();
  }, [prepareCaptcha]);

  const { promoUnauthorizedUser, isCookiePermitted } = useContext(UserContext);
  const { sentCodeTimestamp, setSentCodeTimestamp, sentContact } = useContext(AuthorizationFormContext);
  // Шаг работает только с контактом, на который ушёл последний код: шаг СМС — с телефоном, шаг письма — с почтой
  const contact = sentContact?.loginType === loginType ? sentContact : null;

  const [sendCodeMutation, { loading: codeLoading }] = useMutation<sendCode, sendCodeVariables>(
    SEND_CODE,
    {
      fetchPolicy: "network-only",
    },
  );

  const [code, setCode] = useState<string>(getInitiateCodeState());
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [isUpdateCodeLinkShown, setIsUpdateCodeLinkShown] = useState(false);

  const onAuthorizeSuccess = useCallback(() => {
    setErrorCode(null);
    removeReferralKeyFromCookies();

    if (!isExpert && promoUnauthorizedUser) {
      navigate("/profile/promo-codes");
    }
  }, [isExpert, promoUnauthorizedUser]);

  const onAuthorizeError = useCallback((authError: ApolloError) => {
    setCode(getInitiateCodeState());
    firstCodeInputRef.current?.focus();
    setErrorCode(getErrorCode(authError));
  }, []);

  const { authorize, loading: authLoading } = useAuthorize(onAuthorizeSuccess, onAuthorizeError);

  const { getUtmData } = useManageUtmData();

  const checkTimer = (value: number) => {
    if (!isUpdateCodeLinkShown && value >= 0) {
      setIsUpdateCodeLinkShown(true);
    }
  };

  async function retrySendCode() {
    const yandexCaptchaToken = await executeCaptcha();
    if (contact && yandexCaptchaToken) {
      if (contact.loginType === LoginTypeEnum.LoginByEmail) {
        sendCodeMutation({
          variables: {
            email: contact.value,
            utm: getUtmData(),
            reCaptcha: yandexCaptchaToken,
          },
        });
      }

      if (contact.loginType === LoginTypeEnum.LoginByPhone) {
        sendCodeMutation({
          variables: {
            phone: contact.value,
            utm: getUtmData(),
            reCaptcha: yandexCaptchaToken,
          },
        });
      }
    }

    setIsUpdateCodeLinkShown(false);
    setSentCodeTimestamp(Date.now() + SEND_OTP_CODE_RETRY_DELAY);
  }

  useEffect(() => {
    firstCodeInputRef.current?.focus();
  }, []);

  const submitCode = useCallback(() => {
    if (contact && isCodeLengthValid(code)) {
      const loginInfo: authorizeVariables = {
        code,
      };
      switch (contact.loginType) {
        case LoginTypeEnum.LoginByEmail:
          loginInfo.email = contact.value;
          break;
        case LoginTypeEnum.LoginByPhone:
          loginInfo.phone = contact.value;
          break;
        default:
          break;
      }

      loginInfo.utm = getUtmData();
      loginInfo.referral = getReferralKeyFromCookies();

      authorize(loginInfo);
    }
  }, [authorize, code, contact, getUtmData]);

  useEffect(() => {
    // If first login - code submit will be by button
    if (isFirstLogin) {
      return;
    }
    submitCode();
  }, [isFirstLogin, submitCode]);

  useEffect(() => {
    if (!contact) {
      // На контакт этого шага код не отправляли: возвращаем на форму входа
      // и заменяем шаг в истории, чтобы «Назад» на него не возвращал
      setAuthParamToURL(location, FormTypeEnum.Login, UrlParamKeyEnum.LoginFormModal, true);
    }
  }, [contact, location]);

  if (!contact) {
    return null;
  }

  return (
    <div className="form-with-code-modal">
      <DigitCodeInput
        code={code}
        setCode={setCode}
        disabled={authLoading || codeLoading}
        firstCodeInputRef={firstCodeInputRef}
      />

      {errorCode && (
        <div className="form-with-code-modal__error">
          {getErrorByCode(errorCode, loginType === LoginTypeEnum.LoginByEmail ? "письмо" : "СМС")}
        </div>
      )}
      <div className="auth__text">
        {loginType === LoginTypeEnum.LoginByEmail ? (
          <>
            {`Код отправлен на почту ${contact.value}.`}
            <br />
            Проверьте на всякий случай папку «Спам»
          </>
        ) : (
          `Код отправлен на ${contact.value}`
        )}
      </div>
      <div className="form-with-code-modal__timer-text">
        {isUpdateCodeLinkShown ? (
          <b onClick={retrySendCode}>Получить новый код</b>
        ) : (
          <>
            Получить новый код можно через&nbsp;
            <Timer timestamp={sentCodeTimestamp || undefined} valueCallback={checkTimer} />
          </>
        )}
      </div>
      {isFirstLogin && (
        <>
          <Button
            text="Создать аккаунт"
            isLoading={authLoading}
            size={ButtonSizeEnum.Large}
            color={ButtonColorEnum.Dark}
            onClick={submitCode}
            disabled={!isCodeLengthValid(code)}
          />
          <div className="form-with-code-modal__license">
            <div>
              Нажимая на&nbsp;кнопку «Создать&nbsp;аккаунт», принимаю&nbsp;условия
              {" "}
              <a href="/user-agreement" target="_blank" rel="noreferrer">
                Пользовательского&nbsp;соглашения
              </a>
            </div>
          </div>
        </>
      )}
      <div className="form-with-code-modal__link a">
        <span onClick={() =>
          setAuthParamToURL(location, FormTypeEnum.Troubles)}
        >
          У меня проблемы со входом
        </span>
      </div>
    </div>
  );
};

export default FormWithCodeModal;
